import { copyTab, type CreationContext, type CreationTask, sameContext, SUCCESS_RETENTION_MS } from './creationTask'

const PREFIX = 'mg:leaflet-creation:v1:'

export function taskStorageKey(context: CreationContext, id: string): string {
  return `${PREFIX}${encodeURIComponent(context.country)}:${encodeURIComponent(context.account)}:${id}`
}

export function createTaskPersistence(storage: Storage) {
  function save(task: CreationTask): void {
    const tab = copyTab(task.tab, false)
    // Object URLs and File references are valid only in the current document.
    tab.fileList.forEach(file => delete file.src)
    tab.firstPageFile.forEach(file => delete file.src)
    storage.setItem(taskStorageKey(task.context, task.id), JSON.stringify({ ...task, tab }))
  }

  function read(context: CreationContext): CreationTask[] {
    const tasks: CreationTask[] = []
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index)
      if (!key?.startsWith(PREFIX)) continue
      try {
        const task = JSON.parse(storage.getItem(key) || 'null') as CreationTask | null
        if (!task || task.version !== 1 || !sameContext(context, task.context) || !task.tab || !task.steps) continue
        if (task.completedAt && task.completedAt + SUCCESS_RETENTION_MS <= Date.now()) {
          storage.removeItem(key)
          index--
          continue
        }
        task.tab = copyTab(task.tab, false)
        tasks.push(task)
      } catch {
        // A malformed entry must not prevent recovery of the remaining tasks.
        continue
      }
    }
    return tasks.sort((a, b) => a.createdAt - b.createdAt)
  }

  function remove(task: CreationTask): void {
    storage.removeItem(taskStorageKey(task.context, task.id))
  }

  return { read, save, remove }
}

export type TaskPersistence = ReturnType<typeof createTaskPersistence>
